import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Tag, Select, Space, Popconfirm, Tooltip } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DownloadOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { message } from 'antd';
import {
  fetchDocuments,
  createDocument,
  downloadDocument,
  updateDocumentStatus,
  deleteDocument,
} from './documentsApi';

export default function DocumentsPanel({ pacsOrdId, onOpenDocument }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchDocuments({ pacsOrdId });
      setDocuments(res.data.data || []);
    } catch (err) {
      message.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [pacsOrdId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await createDocument({
        title: `Report - ${new Date().toLocaleString()}`,
        pacsOrdId,
      });
      message.success('Document created');
      await loadDocuments();
      if (res.data?.data?.id) {
        onOpenDocument(res.data.data.id);
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to create document');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (docId, status) => {
    try {
      await updateDocumentStatus(docId, status);
      message.success(`Status updated to ${status}`);
      loadDocuments();
    } catch (err) {
      message.error('Failed to update status');
    }
  };

  const handleDelete = async (docId) => {
    try {
      await deleteDocument(docId);
      message.success('Document deleted');
      loadDocuments();
    } catch (err) {
      message.error('Failed to delete document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      await downloadDocument(doc.id, `${doc.title}.docx`);
    } catch (err) {
      message.error('Failed to download document');
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => (
        <Select
          value={status}
          size="small"
          style={{ width: 120 }}
          onChange={(val) => handleStatusChange(record.id, val)}
        >
          <Select.Option value="draft"><Tag color="blue">Draft</Tag></Select.Option>
          <Select.Option value="finalized"><Tag color="green">Finalized</Tag></Select.Option>
          <Select.Option value="archived"><Tag>Archived</Tag></Select.Option>
        </Select>
      ),
    },
    {
      title: 'Last Edited By',
      dataIndex: 'lastEditedByName',
      key: 'lastEditedByName',
      width: 160,
      render: (val) => val || '-',
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 170,
      render: (val) => val ? new Date(val).toLocaleString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Tooltip title="Open in editor">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onOpenDocument(record.id)}
            />
          </Tooltip>
          <Tooltip title="Download .docx">
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this document?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="m-0 text-lg font-semibold text-white">Documents</h3>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadDocuments} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            loading={creating}
          >
            New Report
          </Button>
        </Space>
      </div>

      <Table
        dataSource={documents}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: 'No documents found' }}
      />
    </div>
  );
}
